import React, { useEffect, useState } from 'react';
import { storeService, type Product } from '../../../../services/storeService';
import { ProductCard } from '../ProductCard';
import { useCart } from '../../../../contexts/ShopCartContext';
import { makeStyles } from '@fluentui/react-components';
import { AlyzeLoadingSpinner } from '../../../shared/AlyzeLoadingSpinner';

const useStyles = makeStyles({
    container: {
        padding: '32px',
        maxWidth: '1200px',
        margin: '0 auto',
    },

    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
    },

    error: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
        textAlign: 'center',
    },

    errorMessage: {
        color: '#e74c3c',
        fontSize: '16px',
    },

    retryButton: {
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        '&:hover': {
            backgroundColor: '#2980b9',
        },
    },

    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        fontSize: '18px',
        color: '#666',
    },

    productsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '24px',
        '@media (max-width: 768px)': {
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '16px',
        },
    },
});

export const ProductGrid: React.FC = () => {
    const styles = useStyles();
    const { addItem } = useCart();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedProducts = await storeService.getAllProducts();
            setProducts(fetchedProducts);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to load products. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleAddToCart = (product: Product, quantity: number = 1) => {
        addItem(product, quantity);
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <AlyzeLoadingSpinner message="Loading products..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <div className={styles.errorMessage}>{error}</div>
                    <button onClick={fetchProducts} className={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.empty}>
                    No products available at this time.
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.productsGrid}>
                {products.map((product) => (
                    <ProductCard
                        key={product.productId}
                        product={product}
                        onAddToCart={handleAddToCart}
                    />
                ))}
            </div>
        </div>
    );
};

// named-only exports
